import { useCallback, useEffect, useMemo, useRef } from "react";
import { playSound, preloadSounds } from "@/lib/soundPlayer";
import { useStore } from "@/stores/useStore";
import {
  resolveSoundPreferences,
  shouldPlayToastSound,
  ToastSoundCategory,
  ToastSoundEffectId,
} from "@/lib/toastSound";

// Module scope, not a useRef: the managers that call this mount inside
// DashboardContainer, which unmounts on route change (open Settings, come
// back). A component-scoped timestamp would reset on remount and let a second
// ding through inside the same second — the same reasoning as
// AutomationToastManager's seenToastKeys. It also makes the limit global to
// every consumer and every category, which is the intent: one ding per
// second app-wide, not per surface.
let lastPlayedAtMs: number | null = null;

// Now that playback goes straight to soundPlayer.ts, the only thing this can
// ever catch is a failed local load (network error, missing file) — never an
// autoplay block, which howler handles by queuing playback rather than
// rejecting. Warn (not debug) so a real failure isn't invisible by default.
const logBlocked = (error: unknown) =>
  console.warn("Toast sound failed to play:", error);

/**
 * Plays one effect right now, with no mute check and no rate limit — the
 * building block under `playToastSound` and the settings dialog's preview.
 *
 * Never throws and never leaves an unhandled rejection: browsers block audio
 * until the page has had a user gesture, and a missed ding must not take the
 * toast down with it.
 * @param {ToastSoundEffectId} effect - The catalog effect to play.
 * @returns {void}
 */
export function playToastSoundEffect(effect: ToastSoundEffectId): void {
  try {
    Promise.resolve(playSound(effect)).catch(logBlocked);
  } catch (error) {
    logBlocked(error);
  }
}

/**
 * Plays the signed-in user's chosen toast sound for one category of toast,
 * honouring that category's on/off switch and the app-wide one-per-second
 * limit.
 *
 * Order matters: a muted category returns BEFORE the rate-limit timestamp is
 * touched, so a muted toast can never swallow an audible one that follows it.
 *
 * Preferences come from the live user doc (`useStore().user`), so a change in
 * the settings dialog applies immediately. Until that doc loads the defaults
 * apply (default effect, everything on) — today's behaviour.
 * @returns {{ playToastSound: (category: ToastSoundCategory) => void }}
 *   `playToastSound` has a stable identity for the life of the component (safe
 *   in effect dependency lists).
 */
export function useToastSound(): {
  playToastSound: (category: ToastSoundCategory) => void;
} {
  const stored = useStore((state) => state.user?.soundPreferences);
  const preferences = useMemo(() => resolveSoundPreferences(stored), [stored]);

  // Read through a ref so playToastSound keeps a stable identity while always
  // seeing the latest preferences.
  const preferencesRef = useRef(preferences);
  preferencesRef.current = preferences;

  // Warm the chosen effect so the first ding doesn't pay the load latency
  // (what useSound did on mount before).
  const { effect } = preferences;
  useEffect(() => {
    Promise.resolve(preloadSounds([effect])).catch((error: unknown) =>
      console.debug("Sound preload failed:", error)
    );
  }, [effect]);

  const playToastSound = useCallback((category: ToastSoundCategory) => {
    const { effect: chosen, enabled } = preferencesRef.current;
    if (!enabled[category]) return;

    const nowMs = Date.now();
    if (!shouldPlayToastSound(lastPlayedAtMs, nowMs)) return;
    lastPlayedAtMs = nowMs;

    playToastSoundEffect(chosen);
  }, []);

  return { playToastSound };
}
